import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FiBriefcase,
  FiClock,
  FiCreditCard,
  FiHome,
  FiLayers,
  FiLock,
  FiMapPin,
  FiMail,
  FiChevronDown,
  FiUser,
} from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";
import "./LoanForm1.css";
import { Link } from "react-router-dom";
import { checkCustomerByPan } from "../../utils/panCustomerCheck";
import { pickAadhaarMobileFromObject } from "../../utils/aadhaarVerifyUtils";
import {
  getApiMessage,
  getPincodeValueFromRow,
  taskApiGetUrl,
  taskApiPostUrl,
} from "../../config/taskApi";
import {
  clearApplyFlowOnSuccess,
  getAadhaarNumberSecure,
  getMobileOtpSecure,
} from "../../utils/applyFlowStorage";
import {
  normalizeEmailInput,
  validateEmail,
} from "../../utils/emailValidation";
import { seedApplyLoanProfileFromAadhaar } from "../../utils/seedApplyLoanProfile";
import { clearApplyLoanDigitapDraft } from "../../utils/applyLoanStorage";
import STATE_CITY from "../../utils/stateCityMapping";
import {
  isPdfFile,
  isPdfPasswordProtected,
} from "../../utils/pdfPasswordCheck";
import {
  pauseEnquiryAutosave,
  scheduleSaveEnquiry,
} from "../../utils/saveEnquiry";
import { useNavigate } from "react-router-dom";

const normalizePlaceName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\bdistrict\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** Merge local mapping cities into TaskApi list so Aadhaar district can always match */
const mergeCitiesWithMapping = (apiCities, stateName) => {
  const list = Array.isArray(apiCities) ? [...apiCities] : [];
  const nState = normalizePlaceName(stateName);
  if (!nState) return list;

  let mapping = null;
  Object.keys(STATE_CITY || {}).forEach((key) => {
    const entry = STATE_CITY[key];
    if (normalizePlaceName(entry?.state_name) === nState) {
      mapping = entry;
    }
  });
  if (!mapping?.cities) return list;

  const byName = new Map();
  list.forEach((c) => {
    const label = normalizePlaceName(
      c.city || c.city_name || c.City || c.name || ""
    );
    if (label) byName.set(label, c);
  });

  Object.entries(mapping.cities).forEach(([cityId, cityName]) => {
    const label = normalizePlaceName(cityName);
    if (!label || byName.has(label)) return;
    const option = { city_id: String(cityId), city: cityName };
    list.push(option);
    byName.set(label, option);
  });

  return list;
};

const IconField = ({ icon: Icon, children, className = "" }) => (
  <div className={`loan-icon-field ${className}`.trim()}>
    <span className="loan-icon-field__icon" aria-hidden="true">
      <Icon />
    </span>
    {children}
  </div>
);

const SelectField = ({ icon: Icon, children, className = "" }) => (
  <div className={`loan-icon-field loan-icon-field--select ${className}`.trim()}>
    <span className="loan-icon-field__icon" aria-hidden="true">
      <Icon />
    </span>
    {children}
    <span className="loan-icon-field__chevron" aria-hidden="true">
      <FiChevronDown />
    </span>
  </div>
);

/** DB gender column expects Male/Female/Other — not M/F/O */
const toSubmitGender = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === "M" || raw === "MALE") return "Male";
  if (raw === "F" || raw === "FEMALE") return "Female";
  if (
    raw === "O" ||
    raw === "OTHER" ||
    raw === "T" ||
    raw === "TRANSGENDER"
  ) {
    return "Other";
  }
  if (raw === "MALE" || raw === "FEMALE" || raw === "OTHER") {
    return raw.charAt(0) + raw.slice(1).toLowerCase();
  }
  return "";
};

const isApiSuccess = (data) => {
  if (!data || typeof data !== "object") return false;

  const success = data.success ?? data.Success;
  const status = data.status ?? data.Status;
  const message = getApiMessage(data).toLowerCase();

  if (
    success === false ||
    success === 0 ||
    success === "0" ||
    success === "false"
  ) {
    return false;
  }

  if (
    status === false ||
    status === 0 ||
    status === "0"
  ) {
    return false;
  }

  if (
    message.includes("already exists") ||
    message.includes("cannot apply") ||
    message.includes("failed")
  ) {
    return false;
  }

  if (
    success === true ||
    success === 1 ||
    success === "1" ||
    success === "true"
  ) {
    return true;
  }

  if (status == 1) return true;

  return false;
};

const PAN_STATUS_VERIFIED = "PAN Verification Complete!";

/** CRM docs_master_id values (LMS document dropdown) */
const DOCUMENT_TYPE_IDS = {
  aadhaarFront: "1", // Aadhar - Front
  aadhaarBack: "2", // Aadhar - Rear
  panCard: "4", // Pan
  bankStatement: "6", // Bank Statement
  utilityBill: "8", // Utility Bill
  salarySlip: "16", // Salary slip -1
};

/** Append file + docs_master_id for submit.php */
const appendDocumentWithId = (formData, fieldName, file, docsMasterId) => {
  if (!file || !docsMasterId) return;
  formData.append(fieldName, file);
  formData.append(`${fieldName}_docs_master_id`, String(docsMasterId));
  formData.append("docs_master_id[]", String(docsMasterId));
  formData.append("docs_master_field[]", fieldName);
};

const isPanNotFoundOnline = (responseCode, responseMessage) =>
  responseCode === "ENI004" ||
  String(responseMessage || "").toLowerCase().includes("no information found");

const isPanAcceptedForSubmit = (status) => {
  const text = String(status || "").toLowerCase();
  return (
    text.includes("complete") ||
    text.includes("continue") ||
    text.includes("re-apply")
  );
};

const DOCUMENT_FILE_RULES = {
  aadhaarFront: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    mimeTypes: ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
    maxSizeMb: 5,
    label: "PDF, JPG, PNG (max 5MB)",
  },
  aadhaarBack: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    mimeTypes: ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
    maxSizeMb: 5,
    label: "PDF, JPG, PNG (max 5MB)",
  },
  panCard: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    mimeTypes: ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
    maxSizeMb: 5,
    label: "PDF, JPG, PNG (max 5MB)",
  },
  bankStatement: {
    accept: ".pdf,application/pdf",
    extensions: ["pdf"],
    mimeTypes: ["application/pdf"],
    maxSizeMb: 10,
    label: "PDF only (max 10MB)",
  },
  salarySlip: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    mimeTypes: ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
    maxSizeMb: 5,
    label: "PDF, JPG, PNG (max 5MB)",
  },
  utilityBill: {
    accept: ".pdf,.jpg,.jpeg,.png",
    extensions: ["pdf", "jpg", "jpeg", "png"],
    mimeTypes: ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
    maxSizeMb: 5,
    label: "PDF, JPG, PNG (max 5MB)",
  },
};

const isAllowedDocumentFile = (file, rules) => {
  if (!file) return false;

  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  if (!rules.extensions.includes(extension)) return false;

  const maxBytes = (rules.maxSizeMb || 5) * 1024 * 1024;
  if (file.size <= 0 || file.size > maxBytes) return false;

  // Some browsers leave type empty; extension already checked above.
  if (file.type && Array.isArray(rules.mimeTypes) && rules.mimeTypes.length) {
    const normalizedType = String(file.type).toLowerCase();
    if (!rules.mimeTypes.includes(normalizedType)) return false;
  }

  return true;
};

const DocumentAcceptHint = ({ rules }) => (
  <span className="loan-field-hint">{rules.label}</span>
);

const Req = () => <span className="req">*</span>;

const SectionHeader = ({ title, description, badge }) => (
  <div className="apply-now1-section-head">
    <div className="apply-now1-section-head__copy">
      <p className="apply-form-block__title">{title}</p>
      {description ? <p>{description}</p> : null}
    </div>
    {badge ? <span className="apply-now1-section-badge">{badge}</span> : null}
  </div>
);

const SelectedFileText = ({ file }) =>
  file ? <div className="apply-now1-doc-file">Selected: {file.name}</div> : null;

const LoanForm1 = ({
  onBack,
  onNext,
  panAlreadyVerified = false,
  collectEmail = false,
  lockIdentityFromDigitap = false,
  leadSource,
}) => {
  const navigate = useNavigate();
  const [pan, setPan] = useState("");
  const [panStatus, setPanStatus] = useState("");
  const [panVerifying, setPanVerifying] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [earlyLeadId, setEarlyLeadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitErrorPlacement, setSubmitErrorPlacement] = useState("submit");
  const submitLockRef = useRef(false);
  const panVerifyLockRef = useRef(false);
  const [autofillData, setAutofillData] = useState(null);
  const [panDetails, setPanDetails] = useState({});
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [loadingCities, setLoadingCities] = useState(false);
  const [statesLoadError, setStatesLoadError] = useState("");
  const [citiesLoadError, setCitiesLoadError] = useState("");
  const [stateError, setStateError] = useState("");
  const [cityError, setCityError] = useState("");
  const [pincode, setPincode] = useState("");
  const [pincodeError, setPincodeError] = useState("");
  const [pincodes, setPincodes] = useState([]);
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const [pincodesLoadError, setPincodesLoadError] = useState("");
  const citiesRequestRef = useRef(0);
  const pincodesRequestRef = useRef(0);
  const salaryPwdCheckRef = useRef(0);

  // Form states
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [obligation, setObligation] = useState("");


  const [incomeError, setIncomeError] = useState("");
  const [loanError, setLoanError] = useState("");
  const [obligationError, setObligationError] = useState("");
  const [panError, setPanError] = useState("");
  const [salarySlipError, setSalarySlipError] = useState("");
  const [bankStatementError, setBankStatementError] = useState("");
  const [salarySlipPasswordError, setSalarySlipPasswordError] = useState("");
  const [utilityBillError, setUtilityBillError] = useState("");
  const [aadhaarFrontError, setAadhaarFrontError] = useState("");
  const [aadhaarBackError, setAadhaarBackError] = useState("");
  const [panCardError, setPanCardError] = useState("");
  const [ackError, setAckError] = useState("");
  const [ackAccepted, setAckAccepted] = useState(false);

  // Document uploads
  const [bankStatement, setBankStatement] = useState(null);
  const [salarySlip, setSalarySlip] = useState(null);
  const [salarySlipPassword, setSalarySlipPassword] = useState("");
  const [salarySlipNeedsPassword, setSalarySlipNeedsPassword] = useState(false);
  const [utilityBill, setUtilityBill] = useState(null);
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [panCardFile, setPanCardFile] = useState(null);

  useEffect(() => {
    let parsed = null;

    // apply-loan: always re-seed Aadhaar profile into loanFormData before paint
    if (panAlreadyVerified || collectEmail) {
      parsed = seedApplyLoanProfileFromAadhaar();
    } else {
      const storedData = localStorage.getItem("loanFormData");
      if (storedData) {
        try {
          parsed = JSON.parse(storedData);
        } catch {
          parsed = null;
        }
      }
    }

    if (!parsed) return;

    setAutofillData(parsed);

    if (collectEmail) {
      const savedEmail = String(
        parsed.email || parsed.email_personal || ""
      ).trim();
      if (savedEmail) setEmail(normalizeEmailInput(savedEmail));
    }

    if (panAlreadyVerified) {
      const savedPan = String(parsed.pan || parsed.pan_verifiedPan || "")
        .toUpperCase()
        .trim();
      if (savedPan.length === 10) {
        setPan(savedPan);
        setPanVerified(true);
        setPanStatus(parsed.panStatus || PAN_STATUS_VERIFIED);
        setPanError("");
        localStorage.setItem("pan_verified", "true");
        setPanDetails({
          pan_responseCode: parsed.pan_responseCode || "",
          pan_responseMessage: parsed.pan_responseMessage || "",
          pan_referenceId: parsed.pan_referenceId || "",
          pan_firstName: parsed.pan_firstName || "",
          pan_lastName: parsed.pan_lastName || "",
          pan_address: parsed.pan_address || "",
          pan_gender: parsed.pan_gender || "",
          pan_aadhaarLinked: parsed.pan_aadhaarLinked || "",
          pan_dob: parsed.pan_dob || "",
          pan_fullName: parsed.pan_fullName || "",
          pan_category: parsed.pan_category || "",
          pan_maskedAadhaarNumber: parsed.pan_maskedAadhaarNumber || "",
          pan_verifiedPan: parsed.pan_verifiedPan || savedPan,
        });
      }
    }
  }, [panAlreadyVerified, collectEmail]);

  useEffect(() => {
    axios
      .get(taskApiGetUrl("getState"))
      .then((res) => {
        if (res.data?.Status === 1 && Array.isArray(res.data.Data)) {
          setStates(res.data.Data);
          setStatesLoadError("");
        } else {
          setStatesLoadError(
            getApiMessage(res.data, "Failed to load states.")
          );
        }
      })
      .catch((err) => {
        console.error("Failed fetching states:", err);
        setStatesLoadError(
          getApiMessage(
            err?.response?.data,
            "Could not load states. Please refresh the page."
          )
        );
      });
  }, []);

  const loadCitiesForState = async (sid) => {
    if (!sid) {
      setCities([]);
      setCitiesLoadError("");
      setLoadingCities(false);
      return [];
    }

    const requestId = ++citiesRequestRef.current;
    setLoadingCities(true);
    setCitiesLoadError("");

    try {
      const resp = await axios.post(
        taskApiPostUrl("getCity"),
        { state_id: String(sid) },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      if (requestId !== citiesRequestRef.current) return [];

      if (resp.data?.Status === 1 && Array.isArray(resp.data.Data)) {
        const stateName =
          states.find((s) => String(s.state_id) === String(sid))?.state || "";
        const cityData = mergeCitiesWithMapping(resp.data.Data, stateName);
        setCities(cityData);
        if (!cityData.length) {
          setCitiesLoadError(
            getApiMessage(resp.data, "No cities found for this state.")
          );
        }
        return cityData;
      }

      setCities([]);
      setCitiesLoadError(
        getApiMessage(resp.data, "Failed to load cities.")
      );
      return [];
    } catch (err) {
      console.error("Failed fetching cities:", err);
      if (requestId !== citiesRequestRef.current) return [];

      setCities([]);
      setCitiesLoadError(
        getApiMessage(
          err?.response?.data,
          "Could not load cities. Please try another state."
        )
      );
      return [];
    } finally {
      if (requestId === citiesRequestRef.current) {
        setLoadingCities(false);
      }
    }
  };

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      setLoadingCities(false);
      return;
    }

    loadCitiesForState(stateId);
  }, [stateId]);

  const loadPincodesForCity = async (cid) => {
    if (!cid) {
      setPincodes([]);
      setPincodesLoadError("");
      setLoadingPincodes(false);
      return [];
    }

    const requestId = ++pincodesRequestRef.current;
    setLoadingPincodes(true);
    setPincodesLoadError("");

    try {
      const resp = await axios.post(
        taskApiPostUrl("getPincodeByCityId"),
        { city_id: String(cid) },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      if (requestId !== pincodesRequestRef.current) return [];

      if (resp.data?.Status === 1 && Array.isArray(resp.data.Data)) {
        const pinData = resp.data.Data.filter((pin) => getPincodeValueFromRow(pin));
        setPincodes(pinData);
        if (!pinData.length) {
          setPincodesLoadError(
            getApiMessage(resp.data, "No pincodes available for this city.")
          );
        }
        return pinData;
      }

      setPincodes([]);
      setPincodesLoadError(
        getApiMessage(resp.data, "Failed to load pincodes.")
      );
      return [];
    } catch (err) {
      console.error("Failed fetching pincodes:", err);
      if (requestId !== pincodesRequestRef.current) return [];

      setPincodes([]);
      setPincodesLoadError(
        getApiMessage(
          err?.response?.data,
          "Could not load pincodes. Please try another city."
        )
      );
      return [];
    } finally {
      if (requestId === pincodesRequestRef.current) {
        setLoadingPincodes(false);
      }
    }
  };

  useEffect(() => {
    if (!cityId) {
      setPincodes([]);
      setLoadingPincodes(false);
      return;
    }

    loadPincodesForCity(cityId);
  }, [cityId]);

  // Soft-save LMS enquiry as email / loan / city fill (never on final Submit)
  useEffect(() => {
    if (!collectEmail && !panAlreadyVerified) return undefined;

    const selectedCity = cities.find(
      (city) => String(city.city_id) === String(cityId)
    );
    const cityName = selectedCity?.city || "";
    const emailTrim = normalizeEmailInput(email);
    const emailOk = emailTrim && !validateEmail(emailTrim);
    const loanOk = loanAmount && !/^0+$/.test(loanAmount);
    const cityOk = Boolean(cityId);

    if (!emailOk && !loanOk && !cityOk) return undefined;

    const patch = { step: "loan_details" };
    if (leadSource) patch.source = leadSource;
    if (emailOk) {
      patch.email = emailTrim;
      patch.email_personal = emailTrim;
    }
    if (loanOk) patch.loan_amount = loanAmount;
    if (cityOk) {
      patch.city_id = String(cityId);
      patch.city_name = cityName;
    }
    const pinTrim = String(pincode || "").trim();
    if (/^\d{6}$/.test(pinTrim)) {
      patch.pincode = pinTrim;
    }

    try {
      const raw = localStorage.getItem("loanFormData");
      const loan = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        "loanFormData",
        JSON.stringify({
          ...loan,
          ...(emailOk
            ? { email: emailTrim, email_personal: emailTrim }
            : {}),
          ...(loanOk ? { loan_amount: loanAmount, loanAmount } : {}),
          ...(cityOk
            ? { city_id: String(cityId), city: cityName, city_name: cityName }
            : {}),
        })
      );
    } catch {
      /* ignore */
    }

    scheduleSaveEnquiry(patch);
    return undefined;
  }, [
    collectEmail,
    panAlreadyVerified,
    leadSource,
    email,
    loanAmount,
    cityId,
    cities,
    pincode,
  ]);

  const handleStateChange = (event) => {
    const sid = event.target.value;
    setStateId(sid);
    setCityId("");
    setPincode("");
    setCities([]);
    setPincodes([]);
    setCitiesLoadError("");
    setPincodesLoadError("");
    setStateError("");
    setCityError("");
    setPincodeError("");
  };

  const handleCityChange = (event) => {
    setCityId(event.target.value);
    setPincode("");
    setPincodes([]);
    setPincodesLoadError("");
    setCityError("");
    setPincodeError("");
  };

  const handlePincodeChange = (event) => {
    setPincode(event.target.value);
    setPincodeError("");
  };

  const getSelectedState = () =>
    states.find((state) => String(state.state_id) === String(stateId));

  const getSelectedCity = () =>
    cities.find((city) => String(city.city_id) === String(cityId));

  // Detect repeated / fake numeric patterns (11111, 1234, 121212, etc.)
  const isFakeRepeatedNumber = (value) => {
    if (!value) return false;

    if (/^(\d)\1+$/.test(value)) return true;
    if (value.length >= 4 && "0123456789".includes(value)) return true;
    if (value.length >= 4 && "9876543210".includes(value)) return true;
    if (/^(\d{2,4})\1+$/.test(value)) return true;

    if (value.length >= 6 && value.length % 2 === 0) {
      const half = value.length / 2;
      if (value.slice(0, half) === value.slice(half)) return true;
    }

    return false;
  };

  // Soft eligibility checks while the user types
  useEffect(() => {
    const incomeNum = Number(monthlyIncome);
    const loanNum = Number(loanAmount);
    const obligationNum = Number(obligation);

    setIncomeError("");
    setLoanError("");
    setObligationError("");

    if (monthlyIncome !== "") {
      if (/^0+$/.test(monthlyIncome)) {
        setIncomeError("Monthly income cannot be zero");
      } else if (isFakeRepeatedNumber(monthlyIncome)) {
        setIncomeError("Please enter your actual monthly salary");
      } else if (incomeNum <= 50000) {
        setIncomeError(
          "Minimum monthly income should be more than ₹50,000"
        );
      }
    }

    if (loanAmount !== "") {
      if (/^0+$/.test(loanAmount)) {
        setLoanError("Loan amount cannot be zero");
      } else if (loanNum >= incomeNum) {
        setLoanError("Loan amount must be less than monthly income");
      }
    }

    if (obligation !== "") {
      if (/^0+$/.test(obligation)) {
        setObligationError("Invalid EMI obligation");
      } else if (isFakeRepeatedNumber(obligation)) {
        setObligationError("Enter valid EMI obligation");
      } else if (obligationNum >= incomeNum) {
        setObligationError(
          "EMI obligation must be less than monthly income"
        );
      }
    }
  }, [monthlyIncome, loanAmount, obligation]);

  // Get Aadhaar data from localStorage
  const getAadhaarData = () => {
    const aadhaarData = localStorage.getItem("aadhaarData");
    return aadhaarData ? JSON.parse(aadhaarData) : null;
  };

  const reportSubmitError = (message, placement = "submit") => {
    const text =
      String(message || "").trim() ||
      "Something went wrong. Please try again.";
    setSubmitError(text);
    setSubmitErrorPlacement(placement);
    setTimeout(() => {
      const el = document.querySelector(
        `[data-submit-error="${placement}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  };

  const resolveErrorPlacement = (apiData, fallback = "submit") => {
    const step = String(apiData?.step || "").toLowerCase();
    const msg = getApiMessage(apiData).toLowerCase();
    if (
      step === "docs" ||
      step.includes("doc") ||
      step.includes("upload") ||
      msg.includes("document")
    ) {
      return "docs";
    }
    if (
      step === "lead_customer" ||
      step.includes("profile") ||
      step.includes("pan") ||
      step.includes("city") ||
      step.includes("state") ||
      msg.includes("city") ||
      msg.includes("district") ||
      msg.includes("state") ||
      msg.includes("pincode") ||
      msg.includes("pan")
    ) {
      return "loan";
    }
    return fallback;
  };

  const handlePanVerify = async (val) => {
    if (!val || val.length !== 10) return;
    if (panVerifyLockRef.current) return;

    panVerifyLockRef.current = true;
    setPanVerifying(true);
    setPanError("");
    setPanStatus("");

    const verifyPanUrl =
      "https://fundswalla.in/loan_backend/verifyPan.php";

    try {
      const customerCheck = await checkCustomerByPan(val);

      if (customerCheck.blocked) {
        setPanDetails({});
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanStatus("");
        setPanError(
          customerCheck.message ||
            "This PAN is already registered. Cannot re-apply."
        );
        return;
      }

      const statusMessage =
        customerCheck.isFresh || !customerCheck.message
          ? PAN_STATUS_VERIFIED
          : customerCheck.message;

      // verifyPan.php reads $_GET['pan']; a POST body is ignored and returns "PAN number required".
      const verifyRes = await axios.get(verifyPanUrl, {
        params: { pan: val },
        timeout: 30000,
      });
      const verifyData = verifyRes.data?.data || {};

      const panInfo = verifyData?.data || {};
      const responseCode = verifyData.responseCode || "";
      const responseMessage = verifyData.responseMessage || "";
      const aadhaarLinked =
        panInfo.aadhaarLinked === true ||
        panInfo.aadhaarLinked === "true" ||
        panInfo.aadhaarLinked === "Y"
          ? "true"
          : panInfo.aadhaarLinked === false ||
            panInfo.aadhaarLinked === "false" ||
            panInfo.aadhaarLinked === "N"
            ? "false"
            : "";

      const nextPanDetails = {
        pan_responseCode: responseCode,
        pan_responseMessage: responseMessage,
        pan_referenceId: verifyData.referenceId || "",
        pan_firstName: panInfo.firstName || "",
        pan_lastName: panInfo.lastName || "",
        pan_address: panInfo.address || "",
        pan_gender: panInfo.gender || "",
        pan_aadhaarLinked: aadhaarLinked,
        pan_dob: panInfo.dob || "",
        pan_fullName: panInfo.fullName || "",
        pan_category: panInfo.category || "",
        pan_maskedAadhaarNumber: panInfo.maskedAadhaarNumber || "",
        pan_verifiedPan: panInfo.pan || val,
      };

      if (responseCode === "SRC001") {
        setPanDetails(nextPanDetails);
        setPanStatus(statusMessage);
        setPanVerified(true);
        localStorage.setItem("pan_verified", "true");
        return;
      }

      if (isPanNotFoundOnline(responseCode, responseMessage)) {
        setPanDetails({});
        setPanStatus("");
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanError(
          responseMessage || "PAN does not exist. Enter your correct PAN."
        );
        return;
      }

      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError(
        responseMessage ||
          getApiMessage(verifyRes.data) ||
          "Please enter a valid PAN number."
      );
    } catch (err) {
      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError(
        getApiMessage(
          err?.response?.data,
          "PAN verification failed. Please try again."
        )
      );
      setPanStatus("");
    } finally {
      panVerifyLockRef.current = false;
      setPanVerifying(false);
    }
  };

  const handleIncomeChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value.length <= 8) {
        setMonthlyIncome(value);
      }
    }
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value.length <= 10) {
        setLoanAmount(value);
      }
    }
  };

  const handleObligationChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value.length <= 7) {
        setObligation(value);
      }
    }
  };

  const handleDocumentChange = (e, rules, setFile, setError) => {
    const file = e.target.files?.[0];
    setSubmitError("");

    if (!file) {
      setFile(null);
      setError("");
      return;
    }

    const maxBytes = (rules.maxSizeMb || 5) * 1024 * 1024;
    if (file.size <= 0) {
      setFile(null);
      setError("Selected file is empty");
      e.target.value = "";
      return;
    }

    if (file.size > maxBytes) {
      setFile(null);
      setError(`File too large. Max ${rules.maxSizeMb || 5}MB allowed`);
      e.target.value = "";
      return;
    }

    if (!isAllowedDocumentFile(file, rules)) {
      setFile(null);
      setError(`Only ${rules.label} files are allowed`);
      e.target.value = "";
      return;
    }

    setError("");
    setFile(file);
  };

  // FINAL SUBMIT - Original working code
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    // Block submit while soft eligibility errors are visible
    if (incomeError || loanError) {
      setSubmitting(false);
      return;
    }

    if (submitting || submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);

    // Validation — reset field errors
    setIncomeError("");
    setLoanError("");
    setObligationError("");
    setSalarySlipError("");
    setBankStatementError("");
    setSalarySlipPasswordError("");
    setUtilityBillError("");
    setAadhaarFrontError("");
    setAadhaarBackError("");
    setPanCardError("");
    setAckError("");
    setEmailError("");

    let hasError = false;

    // Email (collected on this step for apply-loan)
    if (collectEmail) {
      const normalizedEmail = normalizeEmailInput(email);
      const emailValidationError = validateEmail(normalizedEmail);
      if (emailValidationError) {
        setEmailError(emailValidationError);
        hasError = true;
      }
    }

    // PAN (may already be verified on the identity step)
    if (panAlreadyVerified) {
      if (!pan || pan.length !== 10 || !panVerified) {
        setPanError(
          "PAN verification missing. Please go back to identity step."
        );
        hasError = true;
      }
    } else if (panError) {
      hasError = true;
    } else if (
      !pan ||
      pan.length !== 10 ||
      panVerifying ||
      !isPanAcceptedForSubmit(panStatus)
    ) {
      setPanError(
        panVerifying
          ? "PAN verification in progress"
          : "Please enter your PAN and wait for verification"
      );
      hasError = true;
    }

    if (lockIdentityFromDigitap) {
      const aadhaarOk =
        localStorage.getItem("aadhaar_verified") === "true" ||
        autofillData?.digitap_verified === true;
      const fullName = String(
        autofillData?.fullName ||
          autofillData?.firstName ||
          [autofillData?.first_name, autofillData?.middle_name, autofillData?.last_name]
            .filter(Boolean)
            .join(" ") ||
          ""
      ).trim();
      if (!aadhaarOk) {
        setSubmitError(
          "Please complete Aadhaar DigiLocker verification before submitting."
        );
        setSubmitErrorPlacement("loan");
        hasError = true;
      } else if (!fullName || !autofillData?.dob) {
        setSubmitError(
          "Identity details missing. Please verify Aadhaar with DigiLocker again."
        );
        setSubmitErrorPlacement("loan");
        hasError = true;
      }
    }

    if (!monthlyIncome) {
      setIncomeError("Monthly income is required");
      hasError = true;
    }

    if (!loanAmount) {
      setLoanError("Loan amount is required");
      hasError = true;
    }

    if (obligation === "") {
      setObligationError("EMI obligation is required");
      hasError = true;
    }

    if (!aadhaarFront) {
      setAadhaarFrontError("Aadhaar front is required");
      hasError = true;
    }

    if (!aadhaarBack) {
      setAadhaarBackError("Aadhaar back is required");
      hasError = true;
    }

    if (!panCardFile) {
      setPanCardError("PAN card is required");
      hasError = true;
    }

    if (!salarySlip) {
      setSalarySlipError("Salary slip is required");
      hasError = true;
    } else if (
      salarySlipNeedsPassword &&
      !String(salarySlipPassword || "").trim()
    ) {
      setSalarySlipPasswordError(
        "This salary slip PDF is password protected. Please enter the password."
      );
      hasError = true;
    }

    if (!utilityBill) {
      setUtilityBillError("Utility bill is required");
      hasError = true;
    }

    if (!ackAccepted) {
      setAckError(
        "Please accept Terms & Conditions and confirm the declaration"
      );
      hasError = true;
    }

    if (!stateId) {
      setStateError("Please select your state");
      hasError = true;
    }

    if (!cityId) {
      setCityError("Please select your city");
      hasError = true;
    }

    const pinTrim = String(pincode || "").trim();
    if (!pinTrim) {
      setPincodeError("Please select your pincode");
      hasError = true;
    } else if (cityId && pincodes.length > 0) {
      const pinExists = pincodes.some(
        (pin) => getPincodeValueFromRow(pin) === pinTrim
      );
      if (!pinExists) {
        setPincodeError("Please select a valid pincode");
        hasError = true;
      }
    } else if (cityId && !loadingPincodes && pincodes.length === 0) {
      setPincodeError(
        pincodesLoadError || "No pincodes available for selected city"
      );
      hasError = true;
    } else if (!/^\d{6}$/.test(pinTrim)) {
      setPincodeError("Please select a valid pincode");
      hasError = true;
    }

    if (hasError) {
      submitLockRef.current = false;
      setSubmitting(false);
      setTimeout(() => {
        const firstError =
          document.querySelector(".apply-now1-page .loan-field.has-error") ||
          document.querySelector(".apply-now1-page .loan-field-error");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
      return;
    }

    if (!autofillData) {
      reportSubmitError(
        "Autofill data not found. Please restart the process.",
        "loan"
      );
      submitLockRef.current = false;
      setSubmitting(false);
      return;
    }

    // Never call saveEnquiry on final Submit → LEAD-NEW
    pauseEnquiryAutosave();

    // Get Aadhaar data for submission
    const aadhaarData = getAadhaarData();
    
    // Get Aadhaar number directly from localStorage (stored by AadhaarInput.js)
    const aadhaarNumber = String(
      getAadhaarNumberSecure() ||
        aadhaarData?.aadhaarNumber ||
        aadhaarData?.aadhaar_number ||
        ""
    ).replace(/\D/g, "");
    const aadharNoLast4 =
      aadhaarNumber.length >= 4 ? aadhaarNumber.slice(-4) : "";
    const mobileOtpValue = String(
      getMobileOtpSecure() ||
        autofillData?.otp ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem("loanFormData") || "{}")?.otp;
          } catch {
            return "";
          }
        })() ||
        ""
    ).trim();
    const aadhaarMobile =
      localStorage.getItem("aadhaar_mobile") ||
      pickAadhaarMobileFromObject(aadhaarData) ||
      "";
    
    // Keep full Aadhaar only for backend save; UI/storage uses secure helpers.
    const aadhaarNumberForSubmit = aadhaarNumber;

    const isPanStatusTrue =
      panVerified === true ||
      localStorage.getItem("pan_verified") === "true" ||
      panStatus === PAN_STATUS_VERIFIED;

    const panPayloadFields = {
      pan_status: isPanStatusTrue,
      pan_responseCode: panDetails.pan_responseCode || "",
      pan_responseMessage: panDetails.pan_responseMessage || "",
      pan_referenceId: panDetails.pan_referenceId || "",
      pan_firstName: panDetails.pan_firstName || "",
      pan_lastName: panDetails.pan_lastName || "",
      pan_address: panDetails.pan_address || "",
      pan_gender: panDetails.pan_gender || "",
      pan_aadhaarLinked: panDetails.pan_aadhaarLinked || "",
      pan_dob: panDetails.pan_dob || "",
      pan_fullName: panDetails.pan_fullName || "",
      pan_maskedAadhaarNumber: panDetails.pan_maskedAadhaarNumber || "",
      pan_category: panDetails.pan_category || "",
      pan_verifiedPan: panDetails.pan_verifiedPan || pan || "",
    };

    const emailForSubmit = collectEmail
      ? normalizeEmailInput(email)
      : String(autofillData.email || autofillData.email_personal || "").trim();

    if (collectEmail && emailForSubmit) {
      const nextAutofill = {
        ...autofillData,
        email: emailForSubmit,
        email_personal: emailForSubmit,
      };
      setAutofillData(nextAutofill);
      try {
        localStorage.setItem("loanFormData", JSON.stringify(nextAutofill));
      } catch {
        /* ignore */
      }
    }

    const selectedState = getSelectedState();
    const selectedCity = getSelectedCity();
    const submitStateName = selectedState?.state || "";
    const submitCityName = selectedCity?.city || "";
    const submitPincode = String(pincode || "").trim();

    const payload = {
      customer_id: "",
      loan_amount: loanAmount,
      monthly_income: monthlyIncome,
      obligations: obligation,
      state_id: stateId,
      city: cityId,
      pincode: submitPincode,
      first_name: autofillData.firstName,
      middle_name: autofillData.lastName,
      gender: toSubmitGender(autofillData.gender),
      dob: autofillData.dob,
      pan: pan,
      pan_verified: isPanStatusTrue ? "true" : "false",
      mobile: autofillData.mobile,
      email_personal: emailForSubmit,
      source: String(leadSource || "website").trim() || "website",
      mobile_verified: localStorage.getItem("mobile_verified") || "false",
      otp: mobileOtpValue,
      aadhar_no: aadharNoLast4,
      aadhaar_mobile: aadhaarMobile,
      // Add Aadhaar data to payload with better field mapping
      aadhaar_number: aadhaarNumberForSubmit || aadhaarData?.referenceId || "",
      aadhaar_name: aadhaarData?.name || "",
      aadhaar_dob: aadhaarData?.dateOfBirth || aadhaarData?.dob || "",
      aadhaar_gender: aadhaarData?.gender || "",
      // PAN Number details
      ...panPayloadFields,
      aadhaar_address: autofillData?.address || (() => {
        if (aadhaarData?.address) return aadhaarData.address;
        if (aadhaarData?.house) {
          const addressParts = [
            aadhaarData.house,
            aadhaarData?.street || "",
            aadhaarData?.landmark || "",
            aadhaarData?.locality || "",
            aadhaarData?.vtcName || "",
            aadhaarData?.district || "",
            aadhaarData?.state || "",
            aadhaarData?.pincode || ""
          ];
          return addressParts.filter(part => part.trim() !== "").join(", ");
        }
        return "";
      })(),
      aadhaar_pincode: aadhaarData?.pincode || "",
      aadhaar_state: aadhaarData?.state || "",
      aadhaar_district: aadhaarData?.district || aadhaarData?.vtcName || "",
    };

    try {
      let lead_id = earlyLeadId;

      if (!lead_id) {
        const res = await axios.post("https://fundswalla.in/loan_backend/saveLendingPage.php", payload);
        
        // Check if response contains HTML error (PHP fatal error)
        if (typeof res.data === "string" && res.data.includes("<b>Fatal error</b>")) {
          reportSubmitError(
            getApiMessage(res.data, "Server error occurred. Please try again later."),
            "submit"
          );
          return;
        }

        // Check if response is valid JSON/object
        if (!res.data || typeof res.data !== "object") {
          reportSubmitError(
            getApiMessage(res.data, "Server response error. Please try again later."),
            "submit"
          );
          return;
        }

        lead_id = res.data.inserted_id;
        const message = getApiMessage(res.data);

        if (!isApiSuccess(res.data)) {
          reportSubmitError(
            message || "Application could not be submitted. Please try again.",
            resolveErrorPlacement(res.data, "submit")
          );
          return;
        }

        if (!lead_id) {
          reportSubmitError(
            message || "Application could not be submitted. Please try again.",
            resolveErrorPlacement(res.data, "submit")
          );
          return;
        }
      }

      const formData = new FormData();
      formData.append("lead_id", lead_id);
      formData.append("pan", pan);
      formData.append("pan_verified", isPanStatusTrue ? "true" : "false");
      formData.append("pan_status", isPanStatusTrue ? "true" : "false");
      formData.append("pan_responseCode", panPayloadFields.pan_responseCode);
      formData.append("pan_responseMessage", panPayloadFields.pan_responseMessage);
      formData.append("pan_referenceId", panPayloadFields.pan_referenceId);
      formData.append("pan_firstName", panPayloadFields.pan_firstName);
      formData.append("pan_lastName", panPayloadFields.pan_lastName);
      formData.append("pan_address", panPayloadFields.pan_address);
      formData.append("pan_gender", panPayloadFields.pan_gender);
      formData.append("pan_aadhaarLinked", panPayloadFields.pan_aadhaarLinked);
      formData.append("pan_dob", panPayloadFields.pan_dob);
      formData.append("pan_fullName", panPayloadFields.pan_fullName);
      formData.append("pan_maskedAadhaarNumber", panPayloadFields.pan_maskedAadhaarNumber);
      formData.append("pan_category", panPayloadFields.pan_category);
      formData.append("pan_verifiedPan", panPayloadFields.pan_verifiedPan);
      formData.append("mobile", autofillData.mobile);
      formData.append(
        "mobile_verified",
        localStorage.getItem("mobile_verified") || "false"
      );
      formData.append("otp", mobileOtpValue);
      formData.append("aadhar_no", aadharNoLast4);
      formData.append("aadhaar_mobile", aadhaarMobile);
      formData.append("firstName", autofillData.firstName);
      formData.append("lastName", autofillData.lastName);
      formData.append("dob", autofillData.dob);
      formData.append("address", autofillData.address);
      formData.append("stateId", stateId);
      formData.append("cityId", cityId);
      formData.append("pincode", submitPincode);
      formData.append("gender", toSubmitGender(autofillData.gender));
      formData.append("monthlyIncome", monthlyIncome);
      formData.append("monthly_income", monthlyIncome);
      formData.append("loanAmount", loanAmount);
      formData.append("obligation", obligation);
      formData.append("email", emailForSubmit);
      formData.append("state", submitStateName);
      formData.append("city", submitCityName);
      
      // Add Aadhaar data to FormData with better field mapping
      if (aadhaarData) {
        const getAadhaarAddress = () => {
          // First try autofillData address, then Aadhaar data
          if (autofillData?.address) return autofillData.address;
          if (aadhaarData?.address) return aadhaarData.address;
          if (aadhaarData?.house) {
            const addressParts = [
              aadhaarData.house,
              aadhaarData?.street || "",
              aadhaarData?.landmark || "",
              aadhaarData?.locality || "",
              aadhaarData?.vtcName || "",
              aadhaarData?.district || "",
              aadhaarData?.state || "",
              aadhaarData?.pincode || ""
            ];
            return addressParts.filter(part => part.trim() !== "").join(", ");
          }
          return "";
        };

        formData.append("aadhaar_number", aadhaarNumberForSubmit || aadhaarData?.aadhaarNumber || aadhaarData?.aadhaar_number || aadhaarData?.referenceId || "");
        formData.append("aadhaar_name", aadhaarData?.name || "");
        formData.append("aadhaar_dob", aadhaarData?.dateOfBirth || aadhaarData?.dob || "");
        formData.append("aadhaar_gender", aadhaarData?.gender || "");
        formData.append("aadhaar_address", getAadhaarAddress());
        formData.append("aadhaar_pincode", aadhaarData?.pincode || "");
        formData.append("aadhaar_state", aadhaarData?.state || "");
        formData.append("aadhaar_district", aadhaarData?.district || aadhaarData?.vtcName || "");
      }

      if (salarySlipPassword.trim()) {
        formData.append("pwd", salarySlipPassword.trim());
      }

      appendDocumentWithId(
        formData,
        "aadharfront",
        aadhaarFront,
        DOCUMENT_TYPE_IDS.aadhaarFront
      );
      appendDocumentWithId(
        formData,
        "aadhar",
        aadhaarBack,
        DOCUMENT_TYPE_IDS.aadhaarBack
      );
      appendDocumentWithId(
        formData,
        "pan_card",
        panCardFile,
        DOCUMENT_TYPE_IDS.panCard
      );
      appendDocumentWithId(
        formData,
        "bank_statement",
        bankStatement,
        DOCUMENT_TYPE_IDS.bankStatement
      );
      appendDocumentWithId(
        formData,
        "salary_slip",
        salarySlip,
        DOCUMENT_TYPE_IDS.salarySlip
      );
      appendDocumentWithId(
        formData,
        "utility_bill",
        utilityBill,
        DOCUMENT_TYPE_IDS.utilityBill
      );

      formData.append(
        "docs_master_ids",
        JSON.stringify({
          aadharfront: aadhaarFront ? DOCUMENT_TYPE_IDS.aadhaarFront : "",
          aadhar: aadhaarBack ? DOCUMENT_TYPE_IDS.aadhaarBack : "",
          pan_card: panCardFile ? DOCUMENT_TYPE_IDS.panCard : "",
          bank_statement: bankStatement ? DOCUMENT_TYPE_IDS.bankStatement : "",
          salary_slip: salarySlip ? DOCUMENT_TYPE_IDS.salarySlip : "",
          utility_bill: utilityBill ? DOCUMENT_TYPE_IDS.utilityBill : "",
        })
      );

      try {
        const saveRes = await axios.post(
          "https://fundswalla.in/loan_backend/submit.php",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        // Check if response contains HTML error (PHP fatal error)
        if (
          typeof saveRes.data === "string" &&
          saveRes.data.includes("<b>Fatal error</b>")
        ) {
          reportSubmitError(
            getApiMessage(
              saveRes.data,
              "Server error occurred. Please try again later."
            ),
            "submit"
          );
          return;
        }

        // Check if response is valid JSON/object
        if (!saveRes.data || typeof saveRes.data !== "object") {
          reportSubmitError(
            getApiMessage(
              saveRes.data,
              "Server response error. Please try again later."
            ),
            "submit"
          );
          return;
        }

        const submitMessage = getApiMessage(saveRes.data);

        if (!isApiSuccess(saveRes.data)) {
          reportSubmitError(
            submitMessage ||
              "Application could not be submitted. Please try again.",
            resolveErrorPlacement(saveRes.data, "submit")
          );
          return;
        }

        // Enquiry → lead link is done inside saveLendingPage.php (soft try/catch).
        // Do not call saveEarlyMobileLead here — it used to overwrite remarks JSON.

        // Store lead ID briefly for thank-you, then wipe browser KYC/PII
        localStorage.setItem("currentLeadId", lead_id);
        localStorage.removeItem("earlyEnquiryId");
        try {
          sessionStorage.removeItem("applyFlowEarlyEnquiryId");
        } catch {
          /* ignore */
        }
        if (lockIdentityFromDigitap) {
          clearApplyLoanDigitapDraft();
        }
        clearApplyFlowOnSuccess();
        localStorage.setItem("currentLeadId", lead_id);

        // Prefer in-flow thank-you (ApplyNow1 step 4); fallback to /thank-you route
        if (typeof onNext === "function") {
          onNext();
        } else {
          navigate("/thank-you");
        }
        } catch (submitErr) {
          console.error("Submit Error:", submitErr);
          const apiBody = submitErr?.response?.data;
          const submitErrorMessage = getApiMessage(
            apiBody,
            submitErr?.message ||
              "Something went wrong. Please try again later."
          );
          const placement = resolveErrorPlacement(apiBody, "submit");

          // Always show API text when present; network fallback only if none
          if (
            !getApiMessage(apiBody) &&
            (submitErr.code === "ERR_NETWORK" ||
              submitErr.code === "ECONNREFUSED")
          ) {
            reportSubmitError(
              "Network error. Please check your internet connection and try again.",
              "submit"
            );
          } else {
            reportSubmitError(submitErrorMessage, placement);
          }
          return;
        }

    } catch (err) {
      console.error("API Error:", err);
      const apiBody = err?.response?.data;
      const apiMsg = getApiMessage(
        apiBody,
        err?.message || "Something went wrong. Please try again later."
      );
      const placement = resolveErrorPlacement(apiBody, "submit");
      if (
        !getApiMessage(apiBody) &&
        (err.code === "ERR_NETWORK" || err.code === "ECONNREFUSED")
      ) {
        reportSubmitError(
          "Network error. Please check your internet connection and try again.",
          "submit"
        );
      } else {
        reportSubmitError(apiMsg, placement);
      }
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (!autofillData) {
    return (
      <div className="apply-form-head">
        <h2>Loading...</h2>
        <p>Preparing your application</p>
      </div>
    );
  }

  return (
    <div className="loanform-wrapper">
      <div className="loanform-card">
        <form onSubmit={handleSubmit}>
          <div className="apply-form-head">
            <div className="apply-now1-hero">
              <span className="apply-now1-hero__eyebrow">Final Step</span>
              <h2>Complete Application</h2>
              <p>PAN, loan details, and documents.</p>
            </div>
          </div>

          <div className="apply-form-block">
            <SectionHeader
              title="1. Loan Details"
              description=""
              badge=""
            />
            {submitError && submitErrorPlacement === "loan" && (
              <p
                className="loan-field-error apply-submit-inline-error"
                data-submit-error="loan"
                role="alert"
              >
                {submitError}
              </p>
            )}

          {!panAlreadyVerified && (
          <div className="loan-form-grid loan-form-grid--4">
            <div className={`loan-field ${panError ? "has-error" : ""}`}>
              <label>PAN Number <Req /></label>
              <IconField icon={FiCreditCard}>
                <input
                  type="text"
                  maxLength="10"
                  placeholder="Enter PAN"
                  value={pan}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setPan(v);
                    setPanError("");
                    setPanStatus("");
                    setEarlyLeadId(null);
                    if (v.length === 10) handlePanVerify(v);
                  }}
                  disabled={panVerifying}
                />
              </IconField>
              {panVerifying && (
                <p style={{ color: "#1a3648", margin: "4px 0", fontSize: "0.75rem" }}>
                  Verifying PAN...
                </p>
              )}
              {panStatus && !panError && (
                <p
                  style={{
                    color: panStatus.includes("Complete")
                      ? "#67272d"
                      : panStatus.toLowerCase().includes("re-apply")
                        ? "#67272d"
                        : panStatus.toLowerCase().includes("continue")
                          ? "#67272d"
                          : "#64748b",
                    margin: "4px 0",
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                  }}
                >
                  {panStatus}
                </p>
              )}
              {panError && <p className="loan-field-error">{panError}</p>}
            </div>
          </div>
          )}

          {lockIdentityFromDigitap && (
            <div className="loan-form-grid loan-form-grid--2" style={{ marginBottom: "0.75rem" }}>
              <div className="loan-field">
                <label>Full Name <Req /></label>
                <IconField icon={FiUser}>
                  <input
                    type="text"
                    value={String(
                      autofillData?.fullName ||
                        [autofillData?.firstName || autofillData?.first_name, autofillData?.middle_name, autofillData?.lastName || autofillData?.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                        ""
                    )}
                    disabled
                    readOnly
                    placeholder="From DigiLocker"
                  />
                </IconField>
              </div>
            </div>
          )}

          <div
            className={`loan-form-grid ${
              collectEmail ? "loan-form-grid--loan4" : "loan-form-grid--3"
            }`}
          >
            <div className={`loan-field ${incomeError ? "has-error" : ""}`}>
              <label>Monthly Income <Req /></label>
              <IconField icon={FaIndianRupeeSign}>
                <input type="text" value={monthlyIncome} onChange={handleIncomeChange} placeholder="Monthly Income" />
              </IconField>
              {incomeError && <p className="loan-field-error">{incomeError}</p>}
            </div>

            <div className={`loan-field ${loanError ? "has-error" : ""}`}>
              <label>Loan Amount <Req /></label>
              <IconField icon={FiBriefcase}>
                <input type="text" value={loanAmount} onChange={handleLoanAmountChange} placeholder="Required Amount" />
              </IconField>
              {loanError && <p className="loan-field-error">{loanError}</p>}
            </div>

            <div className={`loan-field ${obligationError ? "has-error" : ""}`}>
              <label>EMI Obligations <Req /></label>
              <IconField icon={FiClock}>
                <input
                  type="text"
                  value={obligation}
                  onChange={handleObligationChange}
                  placeholder="Existing EMI"
                />
              </IconField>
              {obligationError && <p className="loan-field-error">{obligationError}</p>}
            </div>

            {collectEmail && (
              <div className={`loan-field ${emailError ? "has-error" : ""}`}>
                <label>Email <Req /></label>
                <IconField icon={FiMail}>
                  <input
                    type="email"
                    value={email}
                    placeholder="yourname@gmail.com"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                  />
                </IconField>
                {emailError && <p className="loan-field-error">{emailError}</p>}
              </div>
            )}
          </div>
          </div>

          <div className="apply-form-block">
            <SectionHeader
              title="2. Your Location"
              description="Select your state, city and pincode to continue."
              badge=""
            />

          <div className="loan-form-grid loan-form-grid--3">
            <div className={`loan-field ${stateError ? "has-error" : ""}`}>
              <label>State <Req /></label>
              <SelectField icon={FiLayers}>
                <select
                  value={stateId}
                  onChange={handleStateChange}
                  className={`apply-flow-input apply-flow-select ${!stateId ? "is-placeholder" : ""}`}
                  disabled={!states.length && !!statesLoadError}
                  required
                >
                  <option value="" disabled hidden>
                    {states.length ? "Select state" : "Loading states..."}
                  </option>
                  {states.map((state) => (
                    <option key={state.state_id} value={String(state.state_id)}>
                      {state.state}
                    </option>
                  ))}
                </select>
              </SelectField>
              {statesLoadError && <p className="loan-field-error">{statesLoadError}</p>}
              {stateError && <p className="loan-field-error">{stateError}</p>}
            </div>

            <div className={`loan-field ${cityError ? "has-error" : ""}`}>
              <label>City / District <Req /></label>
              <SelectField icon={FiHome}>
                <select
                  value={cityId}
                  onChange={handleCityChange}
                  className={`apply-flow-input apply-flow-select ${!cityId ? "is-placeholder" : ""}`}
                  disabled={!stateId || loadingCities}
                  required
                >
                  <option value="" disabled hidden>
                    {!stateId
                      ? "Select state first"
                      : loadingCities
                        ? "Loading cities..."
                        : cities.length
                          ? "Select city"
                          : "No cities available"}
                  </option>
                  {cities.map((city) => (
                    <option key={city.city_id} value={String(city.city_id)}>
                      {city.city}
                    </option>
                  ))}
                </select>
              </SelectField>
              {citiesLoadError && <p className="loan-field-error">{citiesLoadError}</p>}
              {cityError && <p className="loan-field-error">{cityError}</p>}
            </div>

            <div className={`loan-field ${pincodeError ? "has-error" : ""}`}>
              <label>Pincode <Req /></label>
              <SelectField icon={FiMapPin}>
                <select
                  value={pincode}
                  onChange={handlePincodeChange}
                  className={`apply-flow-input apply-flow-select ${!pincode ? "is-placeholder" : ""}`}
                  disabled={!cityId || loadingPincodes || (!pincodes.length && !loadingPincodes)}
                  required
                >
                  <option value="" disabled hidden>
                    {!cityId
                      ? "Select city first"
                      : loadingPincodes
                        ? "Loading pincodes..."
                        : pincodes.length
                          ? "Select pincode"
                          : "No pincodes available"}
                  </option>
                  {pincodes.map((pin) => {
                    const value = getPincodeValueFromRow(pin);
                    return (
                      <option key={pin.pincode_id || value} value={value}>
                        {value}
                      </option>
                    );
                  })}
                </select>
              </SelectField>
              {pincodesLoadError && (
                <p className="loan-field-error">{pincodesLoadError}</p>
              )}
              {pincodeError && <p className="loan-field-error">{pincodeError}</p>}
            </div>
          </div>
          </div>

          <div className="apply-form-block">
            <SectionHeader
              title="3. Upload Documents"
              description=""
              badge=""
            />
            {submitError && submitErrorPlacement === "docs" && (
              <p
                className="loan-field-error apply-submit-inline-error"
                data-submit-error="docs"
                role="alert"
              >
                {submitError}
              </p>
            )}

          <div className="loan-form-grid loan-form-grid--3">

<div className={`loan-field loan-field--upload apply-now1-doc-card ${aadhaarFrontError ? "has-error" : ""}`}>
  <label>Aadhaar Front <Req /></label>
  <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.aadhaarFront} />
  <input
    type="file"
    accept={DOCUMENT_FILE_RULES.aadhaarFront.accept}
    onChange={(e) =>
      handleDocumentChange(
        e,
        DOCUMENT_FILE_RULES.aadhaarFront,
        setAadhaarFront,
        setAadhaarFrontError
      )
    }
  />
  <SelectedFileText file={aadhaarFront} />
  {aadhaarFrontError && <p className="loan-field-error">{aadhaarFrontError}</p>}
</div>

<div className={`loan-field loan-field--upload apply-now1-doc-card ${aadhaarBackError ? "has-error" : ""}`}>
  <label>Aadhaar Back <Req /></label>
  <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.aadhaarBack} />
  <input
    type="file"
    accept={DOCUMENT_FILE_RULES.aadhaarBack.accept}
    onChange={(e) =>
      handleDocumentChange(
        e,
        DOCUMENT_FILE_RULES.aadhaarBack,
        setAadhaarBack,
        setAadhaarBackError
      )
    }
  />
  <SelectedFileText file={aadhaarBack} />
  {aadhaarBackError && <p className="loan-field-error">{aadhaarBackError}</p>}
</div>

<div className={`loan-field loan-field--upload apply-now1-doc-card ${panCardError ? "has-error" : ""}`}>
  <label>PAN Card <Req /></label>
  <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.panCard} />
  <input
    type="file"
    accept={DOCUMENT_FILE_RULES.panCard.accept}
    onChange={(e) =>
      handleDocumentChange(
        e,
        DOCUMENT_FILE_RULES.panCard,
        setPanCardFile,
        setPanCardError
      )
    }
  />
  <SelectedFileText file={panCardFile} />
  {panCardError && <p className="loan-field-error">{panCardError}</p>}
</div>

            <div className={`loan-field loan-field--upload apply-now1-doc-card ${bankStatementError ? "has-error" : ""}`}>
              <label>Bank Statement <span className="loan-field-optional">(Optional)</span></label>
              <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.bankStatement} />
              <div className="loan-upload-stack">
                <input
                  type="file"
                  accept={DOCUMENT_FILE_RULES.bankStatement.accept}
                  onChange={(e) => {
                    handleDocumentChange(
                      e,
                      DOCUMENT_FILE_RULES.bankStatement,
                      setBankStatement,
                      setBankStatementError
                    );
                  }}
                />
                <SelectedFileText file={bankStatement} />
              </div>
              {bankStatementError && <p className="loan-field-error">{bankStatementError}</p>}
            </div>

            <div className={`loan-field loan-field--upload apply-now1-doc-card ${salarySlipError || salarySlipPasswordError ? "has-error" : ""}`}>
              <label>Salary Slip <Req /></label>
              <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.salarySlip} />
              <div className="loan-upload-stack">
                <input
                  type="file"
                  accept={DOCUMENT_FILE_RULES.salarySlip.accept}
                  onChange={async (e) => {
                    handleDocumentChange(
                      e,
                      DOCUMENT_FILE_RULES.salarySlip,
                      setSalarySlip,
                      setSalarySlipError
                    );
                    setSalarySlipPasswordError("");
                    const file = e.target.files?.[0] || null;
                    const checkId = ++salaryPwdCheckRef.current;
                    if (!file) {
                      setSalarySlipPassword("");
                      setSalarySlipNeedsPassword(false);
                      return;
                    }
                    if (!isPdfFile(file)) {
                      setSalarySlipNeedsPassword(false);
                      setSalarySlipPassword("");
                      return;
                    }
                    const needsPwd = await isPdfPasswordProtected(file);
                    if (checkId !== salaryPwdCheckRef.current) return;
                    setSalarySlipNeedsPassword(needsPwd);
                    if (!needsPwd) setSalarySlipPassword("");
                  }}
                />
                <SelectedFileText file={salarySlip} />
                {salarySlipNeedsPassword && (
                <div className="loan-upload-password">
                  <span className="loan-upload-password__label">
                    PDF Password <Req />
                  </span>
                  <IconField icon={FiLock}>
                    <input
                      type="password"
                      className="loan-field-password"
                      placeholder="Enter PDF password"
                      value={salarySlipPassword}
                      onChange={(e) => {
                        setSalarySlipPassword(e.target.value);
                        setSalarySlipPasswordError("");
                      }}
                      autoComplete="new-password"
                    />
                  </IconField>
                </div>
                )}
              </div>
              {salarySlipError && <p className="loan-field-error">{salarySlipError}</p>}
              {salarySlipPasswordError && (
                <p className="loan-field-error">{salarySlipPasswordError}</p>
              )}
            </div>

            <div className={`loan-field loan-field--upload apply-now1-doc-card ${utilityBillError ? "has-error" : ""}`}>
              <label>Utility Bill <Req /></label>
              <DocumentAcceptHint rules={DOCUMENT_FILE_RULES.utilityBill} />
              <input
                type="file"
                accept={DOCUMENT_FILE_RULES.utilityBill.accept}
                onChange={(e) =>
                  handleDocumentChange(
                    e,
                    DOCUMENT_FILE_RULES.utilityBill,
                    setUtilityBill,
                    setUtilityBillError
                  )
                }
              />
              <SelectedFileText file={utilityBill} />
              {utilityBillError && <p className="loan-field-error">{utilityBillError}</p>}
            </div>
          </div>
          </div>

          <div className="apply-form-block apply-loan-ack-block">
            <div className={`loan-ack ${ackError ? "has-error" : ""}`}>
              <label>
                <input
                  type="checkbox"
                  checked={ackAccepted}
                  onChange={(e) => {
                    setAckAccepted(e.target.checked);
                    setAckError("");
                  }}
                />
                <span>
                  I hereby declare that all the above information provided by me
                  is true, correct, and verified to the best of my knowledge. I
                  agree to the{" "}
                  <Link
                    to="/terms-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms &amp; Conditions
                  </Link>{" "}
                  <Req />
                </span>
              </label>
              {ackError && <p className="loan-field-error">{ackError}</p>}
            </div>
          </div>

          {loading && <div className="loader">Processing... Please wait...</div>}

          {submitError && submitErrorPlacement === "submit" && (
            <p
              className="loan-field-error apply-submit-inline-error"
              data-submit-error="submit"
              role="alert"
            >
              {submitError}
            </p>
          )}

          <div className="apply-flow-actions">
            <button
              type="button"
              className="apply-flow-btn apply-flow-btn--secondary"
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="submit"
              className="apply-flow-btn apply-flow-btn--primary"
              disabled={
                submitting ||
                panVerifying ||
                panError !== "" ||
                incomeError !== "" ||
                loanError !== "" ||
                obligationError !== ""
              }
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoanForm1;
